import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Plus, CheckCircle, Trash, MapPin, Clock, Truck } from "@phosphor-icons/react";

/** Onglet Agenda — Matériel à récupérer chez fournisseur.
 *  Liste les pickups sur la fenêtre en cours + bouton "Nouveau" pour l'admin.
 *  Push + WhatsApp CallMeBot auto envoyés à l'assignee côté backend.
 */
export default function SupplierPickupsCard({ start, end, team, onCountChange }) {
  const { isAdmin, user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | pickup
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/supplier-pickups", { params: { start, end } });
      setItems(r.data || []);
      onCountChange?.((r.data || []).length);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (start && end) load(); }, [start, end]);

  const markDone = async (p) => {
    try { await api.patch(`/supplier-pickups/${p.id}`, { status: "picked_up" }); toast.success("Récupéré ✓"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (p) => {
    if (!window.confirm("Supprimer cette récupération fournisseur ?")) return;
    try { await api.delete(`/supplier-pickups/${p.id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const teamMap = Object.fromEntries((team || []).map((u) => [u.id, u]));

  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 via-white/[0.02] to-amber-500/[0.03] border border-amber-500/25 p-4 space-y-3" data-testid="supplier-pickups-card">
      <div className="flex items-center gap-2">
        <Package size={18} weight="duotone" className="text-amber-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/50">À prévoir</div>
          <div className="font-display font-semibold text-sm">{items.length} récupération{items.length > 1 ? "s" : ""} à faire</div>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setEditing("new")}
            className="bg-amber-500 hover:bg-amber-600 text-black h-8 rounded-full text-xs"
            data-testid="supplier-pickup-add-btn"
          >
            <Plus size={12} weight="bold" /> Nouveau
          </Button>
        )}
      </div>

      {loading && items.length === 0 ? (
        <div className="h-12 rounded-lg bg-white/5 animate-pulse" />
      ) : items.length === 0 ? (
        <div className="text-[11px] text-white/40 text-center py-2">Aucune récupération fournisseur planifiée.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((p) => {
            const assignee = teamMap[p.assignee_id];
            const mine = p.assignee_id === user?.id;
            return (
              <div
                key={p.id}
                className={`rounded-xl border p-3 space-y-1.5 ${mine ? "bg-amber-500/10 border-amber-500/40" : "bg-white/[0.03] border-white/10"}`}
                data-testid={`supplier-pickup-${p.id}`}
              >
                <div className="flex items-center gap-2">
                  <Truck size={14} weight="duotone" className="text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{p.supplier_name}</div>
                    <div className="text-[10px] text-white/50 flex items-center gap-2 flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={9} /> {p.pickup_date}{p.pickup_time ? ` · ${p.pickup_time}` : ""}</span>
                      {p.supplier_address && <span className="flex items-center gap-1 truncate"><MapPin size={9} /> {p.supplier_address}</span>}
                    </div>
                  </div>
                  {assignee && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-widest shrink-0"
                      style={{ background: (assignee.color || "#005CFF") + "22", color: assignee.color || "#005CFF" }}
                    >
                      {assignee.name.split(" ")[0]}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/70 whitespace-pre-wrap">{p.items}</div>
                {p.notes && <div className="text-[10px] text-white/40 italic">{p.notes}</div>}
                <div className="flex items-center gap-1 pt-1">
                  {(mine || isAdmin) && (
                    <button
                      onClick={() => markDone(p)}
                      className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1"
                      data-testid={`supplier-pickup-done-${p.id}`}
                    >
                      <CheckCircle size={10} weight="bold" /> Récupéré
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => setEditing(p)}
                        className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                        data-testid={`supplier-pickup-edit-${p.id}`}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => del(p)}
                        className="ml-auto text-white/40 hover:text-red-400 p-1"
                        data-testid={`supplier-pickup-delete-${p.id}`}
                      >
                        <Trash size={11} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <PickupDialog value={editing === "new" ? null : editing} team={team} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}


function PickupDialog({ value, team, onClose, onSaved }) {
  const [form, setForm] = useState(value || {
    supplier_name: "",
    supplier_address: "",
    items: "",
    pickup_date: new Date().toISOString().slice(0, 10),
    pickup_time: "",
    assignee_id: null,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const activeTeam = useMemo(() => (team || []).filter((u) => u.is_active !== false), [team]);

  const save = async () => {
    if (!form.supplier_name.trim() || !form.items.trim() || !form.pickup_date) {
      toast.error("Fournisseur, articles et date requis"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, assignee_id: form.assignee_id === "none" ? null : form.assignee_id };
      if (value?.id) await api.patch(`/supplier-pickups/${value.id}`, payload);
      else await api.post("/supplier-pickups", payload);
      toast.success(value?.id ? "Enregistré" : "Créé + notification envoyée");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-md">
        <DialogHeader><DialogTitle>{value?.id ? "Modifier" : "Nouvelle chose à prévoir"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Fournisseur</Label>
            <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Rexel, Sonepar, Yesss…" className="bg-white/5 border-white/10" data-testid="supplier-pickup-name" />
          </div>
          <div>
            <Label className="text-xs">Adresse (optionnel)</Label>
            <Input value={form.supplier_address || ""} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} placeholder="Rue, ville…" className="bg-white/5 border-white/10" data-testid="supplier-pickup-address" />
          </div>
          <div>
            <Label className="text-xs">Articles à récupérer</Label>
            <Textarea rows={3} value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} placeholder="3× câbles Nexans + 2× disjoncteurs Legrand C16" className="bg-white/5 border-white/10 text-sm" data-testid="supplier-pickup-items" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} className="bg-white/5 border-white/10" data-testid="supplier-pickup-date" />
            </div>
            <div>
              <Label className="text-xs">Heure (optionnel)</Label>
              <Input type="time" value={form.pickup_time || ""} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} className="bg-white/5 border-white/10" data-testid="supplier-pickup-time" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Assigné à (recevra push + WhatsApp CallMeBot)</Label>
            <Select
              value={form.assignee_id || "none"}
              onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? null : v })}
            >
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="supplier-pickup-assignee"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                <SelectItem value="none">— Aucun —</SelectItem>
                {activeTeam.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes (optionnel)</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/10 text-sm" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="supplier-pickup-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
